import { useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { SaveFailedAlert, Sheet } from '../components/States'
import { useEnv } from '../context'
import { isPaused } from '../data/athletes'
import { useInvalidateAthlete } from '../data/hooks'
import { SliceApiError, type Athlete, type GroupRef } from '../data/types'
import { countOf } from './DirectionScreens'

/** Групповое действие со списком спортсменов (S07). */
export type BulkKind = 'move' | 'pause' | 'delete'

type RowState = 'done' | 'skipped' | 'failed'

const NO_GROUP = '__none__'

/**
 * Групповое действие: сначала точный список «кто и как изменится», затем
 * явное подтверждение. Каждый спортсмен - отдельный запрос по очереди; итог
 * по строкам. «Повторить» шлёт только несохранённых: сделанное второй раз не
 * выполняется (а и выполнилось бы - перевод, пауза и удаление на сервере
 * повторяемы: та же группа, пауза не сдвигается, удалённое отвечает 404).
 */
export function BulkSheet({
  kind,
  athletes,
  groups,
  groupOf,
  onClose,
  onFinished,
}: {
  kind: BulkKind
  athletes: Athlete[]
  groups: GroupRef[]
  /** Живая группа спортсмена (удалённая группа - как без группы). */
  groupOf: (a: Athlete) => string | undefined
  onClose: () => void
  /** Всё сделано: выйти из режима выбора. */
  onFinished: () => void
}) {
  const { api, platform } = useEnv()
  const invalidate = useInvalidateAthlete()
  const client = useQueryClient()
  const [target, setTarget] = useState<string>('')
  const [phase, setPhase] = useState<'preview' | 'running' | 'result'>('preview')
  const [states, setStates] = useState<Record<string, RowState>>({})
  const [progress, setProgress] = useState({ done: 0, total: 0 })
  const [locked, setLocked] = useState(false)
  const running = useRef(false)

  const groupName = useMemo(() => new Map(groups.map((g) => [g.id, g.name])), [groups])
  const nameOf = (id: string | undefined) => (id ? groupName.get(id) ?? 'Без группы' : 'Без группы')
  const targetId = target === NO_GROUP ? '' : target

  /** Кого действие не меняет: уже в этой группе, уже на паузе. */
  const unchanged = (a: Athlete): boolean => {
    if (kind === 'move') return (groupOf(a) ?? '') === targetId
    if (kind === 'pause') return isPaused(a)
    return false
  }

  const rows = athletes.map((a) => ({ a, same: kind === 'move' && !target ? false : unchanged(a) }))
  const todo = rows.filter((r) => !r.same)
  const failedIds = Object.entries(states)
    .filter(([, s]) => s === 'failed')
    .map(([id]) => id)

  const run = async (ids: string[]) => {
    // Двойное нажатие и повтор во время выполнения ничего не шлют
    if (running.current || ids.length === 0) return
    running.current = true
    setPhase('running')
    setProgress({ done: 0, total: ids.length })
    setLocked(false)
    platform.haptic('select')
    let n = 0
    for (const id of ids) {
      let state: RowState = 'done'
      try {
        if (kind === 'move') await api.updateAthlete(id, { group_id: targetId })
        else if (kind === 'pause') await api.updateAthlete(id, { paused: true })
        else await api.deleteAthlete(id)
      } catch (e) {
        // Удалено раньше (ответ потерялся или удалили в другом окне) - цель достигнута
        if (kind === 'delete' && e instanceof SliceApiError && e.kind === 'not_found') state = 'done'
        else {
          state = 'failed'
          if (e instanceof SliceApiError && e.kind === 'limit') setLocked(true)
        }
      }
      setStates((prev) => ({ ...prev, [id]: state }))
      n += 1
      setProgress({ done: n, total: ids.length })
    }
    running.current = false
    setPhase('result')
    invalidate()
    if (kind === 'delete') for (const id of ids) client.removeQueries({ queryKey: ['redesign', 'athlete', id] })
  }

  const verb = { move: 'Перевести', pause: 'Поставить на паузу', delete: 'Удалить' }[kind]
  const doneLabel = { move: 'Переведено', pause: 'На паузе', delete: 'Удалено' }[kind]
  const sameLabel = kind === 'move' ? 'Уже в этой группе' : 'Уже на паузе'
  const title = {
    move: 'Перевести в группу',
    pause: 'Поставить на паузу',
    delete: `Удалить ${countOf(athletes.length, 'спортсмена', 'спортсменов', 'спортсменов')}?`,
  }[kind]

  const doneCount = Object.values(states).filter((s) => s === 'done').length
  const busy = phase === 'running'

  const rowCaption = (a: Athlete, same: boolean): string => {
    const st = states[a.id]
    if (st === 'done') return doneLabel
    if (st === 'failed') return 'Не сохранено'
    if (same) return sameLabel
    if (kind === 'move') return target ? `${nameOf(groupOf(a))} -> ${nameOf(targetId || undefined)}` : nameOf(groupOf(a))
    if (kind === 'pause') return `${nameOf(groupOf(a))}: останется в группе`
    return nameOf(groupOf(a))
  }

  return (
    <Sheet title={title} onClose={() => !busy && (phase === 'result' && failedIds.length === 0 ? onFinished() : onClose())}>
      {kind === 'move' && phase === 'preview' && (
        <fieldset className="pc-fieldset" style={{ border: 0, padding: 0, margin: '0 0 8px' }}>
          <legend className="pc-kv__label">В какую группу</legend>
          <ul className="pc-list pc-card pc-group-pick">
            {[...groups.map((g) => [g.id, g.name] as const), [NO_GROUP, 'Без группы'] as const].map(([id, name]) => (
              <li key={id}>
                <label className="pc-check-row">
                  <input type="radio" name="pc-bulk-target" checked={target === id} onChange={() => setTarget(id)} />
                  <span className="pc-list-row__title">{name}</span>
                </label>
              </li>
            ))}
          </ul>
        </fieldset>
      )}

      {kind === 'pause' && phase === 'preview' && (
        <div className="pc-aa-note">
          <span>
            На паузе спортсмен остаётся в группе и в журнале, но в отчёт об оплате и в лимит тарифа не входит. Вернуть - в
            карточке спортсмена.
          </span>
        </div>
      )}
      {kind === 'delete' && phase === 'preview' && (
        <div className="pc-aa-note pc-aa-note--danger">
          <span>
            <b>Карточки пропадут</b> из списков и групп. Отметки посещаемости и оплат останутся в истории. Вернуть из
            приложения нельзя.
          </span>
          <span className="pc-muted">Если просто не ходят какое-то время, лучше поставить на паузу.</span>
        </div>
      )}

      <p className="pc-muted" style={{ margin: '12px 0 4px', fontSize: 14 }} aria-live="polite">
        {busy
          ? `Выполняю: ${progress.done} из ${progress.total}`
          : phase === 'result'
            ? `Готово: ${doneCount}${failedIds.length > 0 ? `, не сохранено: ${failedIds.length}` : ''}`
            : kind === 'move' && !target
              ? `Выбрано: ${athletes.length}`
              : `Изменится: ${todo.length} из ${athletes.length}`}
      </p>
      <ul className="pc-list pc-card" style={{ padding: '0 12px' }} aria-label="Спортсмены">
        {rows.map(({ a, same }) => (
          <li key={a.id}>
            <div className="pc-bulk-row" data-state={states[a.id]}>
              <span className="pc-list-row__title">{a.name}</span>
              <span className="pc-list-row__meta">{rowCaption(a, same)}</span>
            </div>
          </li>
        ))}
      </ul>

      {locked && (
        <div className="pc-note" role="alert">
          Подписка клуба закончилась: клуб сейчас только для чтения. Остальное не сохранено.
        </div>
      )}

      {phase === 'preview' && (
        <button
          type="button"
          className={`pc-btn pc-btn--block ${kind === 'delete' ? 'pc-btn--danger' : 'pc-btn--primary'}`}
          style={{ marginTop: 12 }}
          disabled={todo.length === 0 || (kind === 'move' && !target)}
          onClick={() => void run(todo.map((r) => r.a.id))}
        >
          {`${verb} (${todo.length})`}
        </button>
      )}
      {busy && (
        <button type="button" className="pc-btn pc-btn--block" style={{ marginTop: 12 }} disabled>
          Выполняю…
        </button>
      )}
      {phase === 'result' && failedIds.length > 0 && (
        <>
          <SaveFailedAlert>
            Не сохранено: {failedIds.length}. Сделанное повторно не выполняется - повтор отправит только несохранённых.
          </SaveFailedAlert>
          <button type="button" className="pc-btn pc-btn--block pc-btn--primary" style={{ marginTop: 12 }} onClick={() => void run(failedIds)}>
            Повторить для {failedIds.length}
          </button>
        </>
      )}
      {phase === 'result' && failedIds.length === 0 ? (
        <button type="button" className="pc-btn pc-btn--block pc-btn--primary" style={{ marginTop: 12 }} onClick={onFinished}>
          Готово
        </button>
      ) : (
        <button type="button" className="pc-btn pc-btn--block" disabled={busy} onClick={onClose}>
          {phase === 'result' ? 'Закрыть' : 'Отмена'}
        </button>
      )}
    </Sheet>
  )
}
